import type { ChangeEvent, ReactNode } from "react";
import { useCallback, useId, useMemo, useRef, useState } from "react";

import { Grid, Shelf, Stack } from "../../../layout";
import { Bold, Paragraph, Regular } from "../../../typography";
import { Modal } from "../../Modal";
import { TabbedContainer } from "../../TabbedContainer";
import { COMPONENT_CLASSES, DEFAULT_COMPONENT_CONFIG, DISPLAY_NAMES } from "../constants";
import type { HelpDialogProps, HotkeyEntry } from "../types";
import { fuzzyMatch, groupBy } from "../utils";
import { HotkeyBadge } from "./HotkeyBadge";
import { HotkeyErrorBoundary } from "./HotkeyErrorBoundary";
import { useHotkeyContext } from "./HotkeyProvider";

interface ConflictInfo {
  hasConflict: boolean;
  conflictMessage?: string;
}

const TOGGLE_STYLES: React.CSSProperties = {
  position: "relative",
  width: 40,
  height: 22,
  backgroundColor: "#ccc",
  borderRadius: 11,
  cursor: "pointer",
  transition: "background-color 0.2s",
};

const TOGGLE_ACTIVE_STYLES: React.CSSProperties = {
  ...TOGGLE_STYLES,
  backgroundColor: "#4caf50",
};

const TOGGLE_KNOB_STYLES: React.CSSProperties = {
  position: "absolute",
  top: 2,
  left: 2,
  width: 18,
  height: 18,
  backgroundColor: "#fff",
  borderRadius: "50%",
  transition: "transform 0.2s",
};

const TOGGLE_KNOB_ACTIVE_STYLES: React.CSSProperties = {
  ...TOGGLE_KNOB_STYLES,
  transform: "translateX(18px)",
};

const SETTINGS_ROW_STYLES: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 0",
  borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
};

const BUTTON_STYLES: React.CSSProperties = {
  padding: "8px 16px",
  border: "1px solid #ccc",
  borderRadius: 4,
  background: "#fff",
  cursor: "pointer",
  fontSize: 13,
};

const BUTTON_PRIMARY_STYLES: React.CSSProperties = {
  ...BUTTON_STYLES,
  backgroundColor: "#1976d2",
  color: "#fff",
  border: "none",
};

const FOOTNOTE_STYLES: React.CSSProperties = {
  fontSize: 11,
  color: "#666",
  fontStyle: "italic",
  marginTop: 16,
  paddingTop: 12,
  borderTop: "1px solid rgba(0, 0, 0, 0.1)",
};

export function HelpDialog({
  isOpen = false,
  onOpenChange,
  title = DEFAULT_COMPONENT_CONFIG.helpDialog.defaultTitle,
  groupByCategory = true,
  groupByScope = false,
  showSearch = true,
  filterByActiveScope = false,
  filterFn,
  sortFn,
  renderShortcut,
  zIndex,
}: HelpDialogProps): ReactNode {
  const { manager } = useHotkeyContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<string>("shortcuts");
  const [settingsRefresh, setSettingsRefresh] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tabIdPrefix = useId();

  const allHotkeys = manager.getHotkeys?.() || [];
  const preferences = manager.getPreferences?.();

  // Get validation results with conflict detection
  const conflictMap = useMemo(() => {
    const map = new Map<string, ConflictInfo>();

    try {
      const validation = manager.validateAll?.();
      if (validation?.warnings) {
        for (const warning of validation.warnings) {
          if (warning.message.includes("Conflicting") && warning.affectedHotkeys) {
            for (const hotkeyId of warning.affectedHotkeys) {
              map.set(hotkeyId, {
                hasConflict: true,
                conflictMessage: warning.message,
              });
            }
          }
        }
      }
    } catch {
      // validateAll may not be available
    }

    return map;
  }, [manager]);

  const filteredHotkeys = useMemo(() => {
    const hotkeys = manager.getHotkeys?.() || [];
    const activeScope = filterByActiveScope ? manager.getActiveScope?.() : null;

    // Single pass combining all filters for O(n) instead of O(3n)
    const filtered = hotkeys.filter((h: HotkeyEntry) => {
      // Scope filter
      if (filterByActiveScope && activeScope && h.scope !== activeScope) return false;

      // Custom filter
      if (filterFn && !filterFn(h)) return false;

      // Search filter
      if (searchQuery) {
        const titleMatch = fuzzyMatch(searchQuery, h.config?.description || "");
        const comboMatch = fuzzyMatch(searchQuery, h.config?.key || "");
        if (!titleMatch.matches && !comboMatch.matches) return false;
      }

      return true;
    });

    // Sort if needed
    return sortFn ? [...filtered].sort(sortFn) : filtered;
  }, [allHotkeys, filterByActiveScope, filterFn, searchQuery, sortFn, manager]);

  const groupedHotkeys = useMemo(() => {
    if (groupByScope) {
      return groupBy(filteredHotkeys, (h: HotkeyEntry) => h.scope || "global");
    }
    if (groupByCategory) {
      return groupBy(filteredHotkeys, (h: HotkeyEntry) => h.config?.category || "Other");
    }
    return { All: filteredHotkeys };
  }, [filteredHotkeys, groupByCategory, groupByScope]);

  // Settings handlers
  const handleToggleShortcut = useCallback(
    (hotkeyId: string, currentlyEnabled: boolean) => {
      if (!preferences) return;

      if (currentlyEnabled) {
        preferences.disableShortcut?.(hotkeyId);
      } else {
        preferences.enableShortcut?.(hotkeyId);
      }
      setSettingsRefresh((r) => r + 1);
    },
    [preferences],
  );

  const handleResetAll = useCallback(() => {
    if (!preferences) return;
    if (window.confirm("Reset all shortcut settings to defaults?")) {
      preferences.reset?.();
      setSettingsRefresh((r) => r + 1);
    }
  }, [preferences]);

  const handleExport = useCallback(() => {
    if (!preferences) return;
    const json = preferences.exportPreferences?.();
    if (json) {
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "hotkey-preferences.json";
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [preferences]);

  const handleImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !preferences) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const json = event.target?.result as string;
        if (json) {
          const success = preferences.importPreferences?.(json);
          if (success) {
            setSettingsRefresh((r) => r + 1);
          } else {
            alert("Failed to import preferences. Please check the file format.");
          }
        }
      };
      reader.readAsText(file);

      // Reset input so same file can be selected again
      e.target.value = "";
    },
    [preferences],
  );

  // Get disabled shortcuts for settings tab
  const disabledShortcuts = useMemo(() => {
    // Trigger re-render when settingsRefresh changes
    void settingsRefresh;
    return new Set(preferences?.getDisabledShortcuts?.() || []);
  }, [preferences, settingsRefresh]);

  const renderShortcutsTab = () => (
    <Stack>
      <Paragraph>Below is the list of keyboard shortcuts available on the current page.</Paragraph>

      {showSearch && (
        <input
          type="text"
          className={COMPONENT_CLASSES.helpDialog.search}
          placeholder="Search shortcuts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      )}

      {Object.keys(groupedHotkeys).length === 0 ? (
        <div className={COMPONENT_CLASSES.helpDialog.empty}>No shortcuts found {searchQuery && `matching "${searchQuery}"`}</div>
      ) : (
        <Grid>
          <Grid.Spread columns={6}>
            {Object.entries(groupedHotkeys).map(([category, hotkeys]) => (
              <div key={category} className={COMPONENT_CLASSES.helpDialog.category}>
                <Bold>{category.toUpperCase()}</Bold>
                <Stack spacing="mini">
                  {(hotkeys as HotkeyEntry[]).map((hotkey) => {
                    if (renderShortcut) {
                      return <div key={hotkey.id || hotkey.config?.key}>{renderShortcut(hotkey)}</div>;
                    }

                    const config = hotkey.config;
                    if (!config) return null;
                    const combo = `${config.modifiers?.join("+") || ""}${config.modifiers?.length ? "+" : ""}${config.key}`;
                    const isDisabled = !hotkey.enabled || disabledShortcuts.has(hotkey.id || "");

                    // Check for conflicts using the hotkey's description as identifier
                    const hotkeyId = `${combo}:${config.description || config.title || ""}`;
                    const conflictInfo = conflictMap.get(hotkeyId);
                    const hasConflict = conflictInfo?.hasConflict ?? false;

                    return (
                      <Shelf
                        key={hotkey.id || combo}
                        spacing="mini"
                        className={COMPONENT_CLASSES.helpDialog.shortcut}
                        style={isDisabled ? { opacity: 0.5, filter: "grayscale(100%)" } : undefined}
                      >
                        <div style={{ flex: 1 }}>
                          {hasConflict && (
                            <span
                              title={conflictInfo?.conflictMessage || "This shortcut conflicts with another"}
                              style={{ cursor: "help", marginRight: "0.5ch" }}
                            >
                              ⚠️
                            </span>
                          )}
                          <Bold>{config.description || config.title || "Unnamed"}</Bold>
                          {isDisabled && <span style={{ fontSize: "0.75em", marginLeft: "0.5ch" }}>(disabled)</span>}
                        </div>
                        <HotkeyBadge combo={combo} theme="minimal" size="sm" disabled={isDisabled} />
                      </Shelf>
                    );
                  })}
                </Stack>
              </div>
            ))}
          </Grid.Spread>
        </Grid>
      )}
    </Stack>
  );

  const renderSettingsTab = () => (
    <Stack spacing="medium">
      <Paragraph>Enable or disable individual keyboard shortcuts. Disabled shortcuts will not trigger their actions.</Paragraph>

      <div style={{ maxHeight: 300, overflowY: "auto" }}>
        {allHotkeys.map((hotkey: HotkeyEntry) => {
          const config = hotkey.config;
          if (!config) return null;
          const combo = `${config.modifiers?.join("+") || ""}${config.modifiers?.length ? "+" : ""}${config.key}`;
          const hotkeyId = hotkey.id || `${combo}:${config.description || ""}`;
          const isEnabled = !disabledShortcuts.has(hotkeyId);

          return (
            <div key={hotkeyId} style={SETTINGS_ROW_STYLES}>
              <Shelf spacing="small">
                <HotkeyBadge combo={combo} theme="minimal" size="sm" disabled={!isEnabled} />
                <div>
                  <Bold>{config.description || config.title || "Unnamed"}</Bold>
                  {!isEnabled && <Regular style={{ fontSize: 11, color: "#999", marginLeft: 8 }}>(disabled)</Regular>}
                </div>
              </Shelf>
              <div
                style={isEnabled ? TOGGLE_ACTIVE_STYLES : TOGGLE_STYLES}
                onClick={() => handleToggleShortcut(hotkeyId, isEnabled)}
                role="switch"
                aria-checked={isEnabled}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleToggleShortcut(hotkeyId, isEnabled);
                  }
                }}
              >
                <div style={isEnabled ? TOGGLE_KNOB_ACTIVE_STYLES : TOGGLE_KNOB_STYLES} />
              </div>
            </div>
          );
        })}
      </div>

      <Shelf spacing="small">
        <button type="button" style={BUTTON_STYLES} onClick={handleResetAll}>
          Reset All to Defaults
        </button>
        <button type="button" style={BUTTON_PRIMARY_STYLES} onClick={handleExport}>
          Export Settings
        </button>
        <button type="button" style={BUTTON_STYLES} onClick={handleImport}>
          Import Settings
        </button>
        <input ref={fileInputRef} type="file" accept=".json" style={{ display: "none" }} onChange={handleFileChange} />
      </Shelf>

      <div style={FOOTNOTE_STYLES}>Settings are saved to this browser only.</div>
    </Stack>
  );

  return (
    <HotkeyErrorBoundary>
      <Modal visible={isOpen} size="wide" title={title} onClose={() => onOpenChange?.(false)}>
        <div className={COMPONENT_CLASSES.helpDialog.container} style={{ zIndex }}>
          <TabbedContainer selectedTab={activeTab} onSelectedTabChange={(id) => id && setActiveTab(id)}>
            <TabbedContainer.TabPane id={`${tabIdPrefix}-shortcuts`} label="Shortcuts">
              {renderShortcutsTab()}
            </TabbedContainer.TabPane>
            <TabbedContainer.TabPane id={`${tabIdPrefix}-settings`} label="Settings">
              {renderSettingsTab()}
            </TabbedContainer.TabPane>
          </TabbedContainer>
        </div>
      </Modal>
    </HotkeyErrorBoundary>
  );
}

HelpDialog.displayName = DISPLAY_NAMES.HelpDialog;
